/**
 * supabaseDsgvo.ts — DSGVO-Self-Service-Schicht für Mandanten + Berater.
 *
 * Bedient Migrations 0019 (dsgvo_requests + RPCs) und 0020
 * (case_clarification_counts View für N+1-Vermeidung).
 *
 * Public API:
 *   - exportCaseDataForSubject(caseId)            → Art-15-JSON-Export
 *   - downloadCaseDataAsJsonFile(caseId)          → Browser-Download (Convenience)
 *   - requestCaseDeletion(caseId, reason?)        → Art-17-Anfrage
 *   - listDsgvoRequestsForCase(caseId)            → Berater + Mandant sehen Liste
 *   - fulfillDeletionRequest(requestId)           → Berater bestätigt → Soft-Delete
 *   - rejectDsgvoRequest(requestId, response)     → Berater lehnt ab
 *   - withdrawConsent(caseId, consentPurpose, …)  → DSGVO-Anfrage + consent_records-Update
 *   - getClarificationCountsForCases(caseIds)     → View aus 0020 (single Roundtrip)
 *
 * Sicherheits-Garantien (DB):
 *   - SELECT/INSERT/UPDATE auf dsgvo_requests über can_access_case().
 *   - INSERT enforce: requested_by = auth.uid().
 *   - UPDATE: nur Berater/Admin (RLS).
 *   - export_case_data_for_subject ist SECURITY DEFINER + Auth-Check innen.
 *   - request_case_deletion + fulfill_deletion_request analog.
 *
 * Was diese Schicht NICHT tut:
 *   - Kein eigenes Crypto — sensitive_payload bleibt verschlüsselt im Export,
 *     UI-Layer entschlüsselt mit dem User-Key.
 *   - Keine direkte Email-Pflege — die DB-Trigger aus 0021 queuen die Mails,
 *     wenn email_outbox existiert.
 */

import { getSupabaseClient } from './supabase';
import type {
  CaseClarificationCountsRow,
  ConsentPurpose,
  ConsentRecordRow,
  Database,
  DsgvoRequestRow,
  DsgvoRequestStatus,
  DsgvoRequestType,
} from './supabaseTypes';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const REASON_MAX_LEN = 2000;
const RESPONSE_MAX_LEN = 4000;

export type DsgvoRequest = DsgvoRequestRow;
export type { DsgvoRequestType, DsgvoRequestStatus };

// ============================================================================
// Datenexport (Art. 15)
// ============================================================================

/**
 * Vollständiger Art-15-Export-Payload. Spiegelt das jsonb-Return-Schema von
 * `public.export_case_data_for_subject` (Migration 0019).
 *
 * Sensible Wizard-Felder (sensitive_payload, raw_bwa_payload) bleiben
 * verschlüsselt — der UI-Layer entschlüsselt mit dem User-Crypto-Key.
 */
export interface CaseDataExport {
  readonly exported_at: string;
  readonly case_id: string;
  readonly subject_user_id: string;
  readonly case: Record<string, unknown>;
  readonly documents: ReadonlyArray<Record<string, unknown>>;
  readonly dimension_data: ReadonlyArray<Record<string, unknown>>;
  readonly financial_dataset: ReadonlyArray<Record<string, unknown>>;
  readonly clarifications: ReadonlyArray<Record<string, unknown>>;
  readonly consent_records: ReadonlyArray<Record<string, unknown>>;
  readonly audit_log_summary: {
    readonly total_entries: number;
    readonly first_entry_at: string | null;
    readonly last_entry_at: string | null;
    readonly actions_count: Record<string, number>;
    readonly note: string;
  };
}

/**
 * Holt einen vollständigen DSGVO-Art-15-Datenexport für einen Case.
 * Liefert das jsonb-Result als typisiertes Objekt.
 *
 * Backend prüft auth + can_access_case(); ohne Berechtigung wirft die
 * RPC einen 'insufficient_privilege'-Fehler.
 */
export async function exportCaseDataForSubject(caseId: string): Promise<CaseDataExport> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('exportCaseDataForSubject: caseId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  // Cast `as never`: bekannter supabase-js-v2-Type-Inferenz-Quirk bei
  // RPC-Args mit einzelnem Parameter (siehe analoges Pattern in
  // supabaseInvitations.redeemInvitation). Runtime-Validation erfolgt
  // auf der DB-Seite (RPC validiert Args + Berechtigung).
  const { data, error } = await sb.rpc('export_case_data_for_subject', {
    p_case_id: caseId,
  } as never);

  if (error) {
    throw new Error(`exportCaseDataForSubject [${error.code ?? '??'}]: ${error.message}`);
  }
  if (data === null || typeof data !== 'object') {
    throw new Error(
      `exportCaseDataForSubject: unerwartete Antwort vom Server: ${JSON.stringify(data)}`,
    );
  }

  // Server liefert exakt das CaseDataExport-Schema (siehe Migration 0019,
  // export_case_data_for_subject); der Receiver-Typ akzeptiert data direkt.
  return data;
}

/**
 * Convenience: ruft exportCaseDataForSubject und triggert einen
 * Browser-Download als JSON-Datei.
 *
 * Reine Browser-Funktion — wirft im Node-Umfeld (kein document/window).
 * Der Dateiname enthält case_id (kurz) + ISO-Date für klare Identifizierung.
 */
export async function downloadCaseDataAsJsonFile(caseId: string): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error(
      'downloadCaseDataAsJsonFile: nur im Browser aufrufbar (window/document nicht verfügbar)',
    );
  }

  const exportData = await exportCaseDataForSubject(caseId);

  // Pretty-printed JSON für Mensch-Lesbarkeit.
  const jsonStr = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  try {
    const dateIso = new Date().toISOString().slice(0, 10);
    const shortId = caseId.slice(0, 8);
    const filename = `dsgvo-datenexport-${shortId}-${dateIso}.json`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ============================================================================
// Löschanfrage (Art. 17)
// ============================================================================

export interface RequestCaseDeletionInput {
  readonly caseId: string;
  readonly reason?: string;
}

/**
 * Beantragt die Löschung eines Case. Wrapped public.request_case_deletion().
 *
 * RPC-Verhalten:
 *   - Doppel-Anfrage-Schutz: wirft 'unique_violation' wenn schon offene
 *     deletion-Anfrage besteht. Wir wandeln in lesbare Meldung um.
 *   - reason ist optional, max 2000 Zeichen.
 *
 * Returns das frisch angelegte DsgvoRequest-Objekt (zwei Roundtrips: RPC
 * gibt id zurück, dann SELECT für volles Objekt). Falls SELECT fehlschlägt
 * (RLS-Edge), bauen wir das Objekt aus dem Input nach.
 */
export async function requestCaseDeletion(caseId: string, reason?: string): Promise<DsgvoRequest> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('requestCaseDeletion: caseId ist kein UUID-Format');
  }
  if (reason !== undefined) {
    const trimmed = reason.trim();
    if (trimmed.length > REASON_MAX_LEN) {
      throw new Error(
        `requestCaseDeletion: reason ist auf ${REASON_MAX_LEN.toString()} Zeichen begrenzt`,
      );
    }
  }

  const sb = getSupabaseClient();
  const args =
    reason !== undefined && reason.trim().length > 0
      ? { p_case_id: caseId, p_reason: reason.trim() }
      : { p_case_id: caseId };

  const { data, error } = await sb.rpc('request_case_deletion', args as never);
  if (error) {
    // Doppel-Anfrage-Schutz hübsch übersetzen
    if (error.code === '23505' || /bereits eine offene Löschanfrage/i.test(error.message)) {
      throw new Error(
        'Es gibt bereits eine offene Löschanfrage für dieses Mandat. Bitte warten Sie auf Bearbeitung oder kontaktieren Sie Ihren Berater.',
      );
    }
    throw new Error(`requestCaseDeletion [${error.code ?? '??'}]: ${error.message}`);
  }
  // RPC-Inferenz fällt bei single-Arg-as-never auf `never` zurück; wir
  // narrowen über unknown und runtime-checken (UUID-Form).
  const newId: unknown = data;
  if (typeof newId !== 'string' || !UUID_RE.test(newId)) {
    throw new Error(
      `requestCaseDeletion: unerwartete Antwort vom Server: ${JSON.stringify(newId)}`,
    );
  }

  // Volles Objekt nachladen, RLS lässt es durch (eigene Anfrage)
  const { data: row, error: selectErr } = await sb
    .from('dsgvo_requests')
    .select('*')
    .eq('id', newId)
    .single();
  if (selectErr || row === null) {
    throw new Error(
      `requestCaseDeletion: Anfrage angelegt (id=${newId}), Detail-SELECT fehlgeschlagen: ${selectErr?.message ?? 'no data'}`,
    );
  }
  return row;
}

/**
 * Listet alle DSGVO-Anfragen eines Case. RLS filtert automatisch
 * (can_access_case): Mandant sieht eigene + Berater sieht alle des Case.
 */
export async function listDsgvoRequestsForCase(caseId: string): Promise<DsgvoRequest[]> {
  if (!UUID_RE.test(caseId)) return [];

  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('dsgvo_requests')
    .select('*')
    .eq('case_id', caseId)
    .order('requested_at', { ascending: false });

  if (error) {
    throw new Error(`listDsgvoRequestsForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Bestätigt eine Löschanfrage (Berater/Admin only). Wrapped die SECURITY-
 * DEFINER-RPC public.fulfill_deletion_request, die Soft-Delete des Case
 * setzt und den Request als fulfilled markiert.
 *
 * Returns das aktualisierte DsgvoRequest-Objekt für UI-Refresh.
 */
export async function fulfillDeletionRequest(requestId: string): Promise<DsgvoRequest> {
  if (!UUID_RE.test(requestId)) {
    throw new Error('fulfillDeletionRequest: requestId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const { error } = await sb.rpc('fulfill_deletion_request', {
    p_request_id: requestId,
  } as never);
  if (error) {
    throw new Error(`fulfillDeletionRequest [${error.code ?? '??'}]: ${error.message}`);
  }

  // Aktuellen Stand zurückgeben
  const { data: row, error: selectErr } = await sb
    .from('dsgvo_requests')
    .select('*')
    .eq('id', requestId)
    .single();
  if (selectErr || row === null) {
    throw new Error(
      `fulfillDeletionRequest: ausgeführt (id=${requestId}), Detail-SELECT fehlgeschlagen: ${selectErr?.message ?? 'no data'}`,
    );
  }
  return row;
}

/**
 * Lehnt eine DSGVO-Anfrage ab (Berater-only via RLS). Setzt status='rejected',
 * handled_by + handled_at + response_text. Trigger 0019 enforced, dass
 * immutables (request_type, requested_by, etc.) unverändert bleiben.
 */
export async function rejectDsgvoRequest(
  requestId: string,
  response: string,
): Promise<DsgvoRequest> {
  if (!UUID_RE.test(requestId)) {
    throw new Error('rejectDsgvoRequest: requestId ist kein UUID-Format');
  }
  const trimmedResponse = response.trim();
  if (trimmedResponse.length === 0 || trimmedResponse.length > RESPONSE_MAX_LEN) {
    throw new Error(
      `rejectDsgvoRequest: response muss 1..${RESPONSE_MAX_LEN.toString()} Zeichen sein`,
    );
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`rejectDsgvoRequest: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('rejectDsgvoRequest: kein authentifizierter User');
  }

  const update: Database['public']['Tables']['dsgvo_requests']['Update'] = {
    status: 'rejected',
    handled_by: uid,
    handled_at: new Date().toISOString(),
    response_text: trimmedResponse,
  };

  const { data, error } = await sb
    .from('dsgvo_requests')
    .update(update as never)
    .eq('id', requestId)
    // Defensiv: nur ablehnen, was noch offen ist
    .in('status', ['open', 'in_progress'])
    .select('*')
    .single();

  if (error) {
    throw new Error(`rejectDsgvoRequest [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

// ============================================================================
// Einwilligungs-Widerruf
// ============================================================================

/**
 * Widerruft eine Einwilligung. Doppel-Mechanismus:
 *   1. Legt eine DSGVO-Anfrage vom Typ 'consent_withdrawal' an (für
 *      Berater-Bearbeitung + Audit-Trail).
 *   2. Aktualisiert parallel den consent_records-Eintrag (withdrawn_at +
 *      withdrawal_reason). Trigger 0006 enforced, dass nur diese beiden
 *      Felder geändert werden — alles andere ist immutable.
 *
 * Wenn kein consent_record für (user, case, purpose) existiert, wird nur
 * die DSGVO-Anfrage angelegt — der Berater muss dann manuell prüfen.
 */
export async function withdrawConsent(
  caseId: string,
  consentPurpose: ConsentPurpose,
  reason?: string,
): Promise<DsgvoRequest> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('withdrawConsent: caseId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`withdrawConsent: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('withdrawConsent: kein authentifizierter User');
  }

  const trimmedReason = reason?.trim();
  const reasonForRequest =
    trimmedReason !== undefined && trimmedReason.length > 0
      ? `[purpose=${consentPurpose}] ${trimmedReason}`
      : `[purpose=${consentPurpose}]`;

  if (reasonForRequest.length > REASON_MAX_LEN) {
    throw new Error(
      `withdrawConsent: kombinierter reason ist zu lang (${reasonForRequest.length.toString()} > ${REASON_MAX_LEN.toString()})`,
    );
  }

  // 1. DSGVO-Anfrage anlegen
  const insertPayload: Database['public']['Tables']['dsgvo_requests']['Insert'] = {
    case_id: caseId,
    request_type: 'consent_withdrawal',
    requested_by: uid,
    reason: reasonForRequest,
  };
  const insertResult = await sb
    .from('dsgvo_requests')
    .insert(insertPayload as never)
    .select('*')
    .single();
  if (insertResult.error) {
    throw new Error(`withdrawConsent [insert]: ${insertResult.error.message}`);
  }
  const requestRow = insertResult.data as DsgvoRequestRow | null;
  if (requestRow === null) {
    throw new Error('withdrawConsent: INSERT returned no row (unexpected)');
  }

  // 2. consent_records UPDATE (best-effort — Existenz nicht garantiert).
  //    Wir suchen aktive Consents (granted=true, withdrawn_at IS NULL).
  const consentUpdate: Database['public']['Tables']['consent_records']['Update'] = {
    withdrawn_at: new Date().toISOString(),
    withdrawal_reason:
      trimmedReason !== undefined && trimmedReason.length > 0
        ? trimmedReason
        : `User-initiated withdrawal via dsgvo_request ${requestRow.id}`,
  };
  const { error: updateErr } = await sb
    .from('consent_records')
    .update(consentUpdate as never)
    .eq('case_id', caseId)
    .eq('user_id', uid)
    .eq('purpose', consentPurpose)
    .is('withdrawn_at', null);

  if (updateErr) {
    // Wir loggen nur — die DSGVO-Anfrage ist angelegt, der Berater wird sie sehen.
    console.warn('withdrawConsent: consent_records-Update fehlgeschlagen, DSGVO-Anfrage bleibt', {
      code: updateErr.code,
      message: updateErr.message,
    });
  }

  return requestRow;
}

// ============================================================================
// Clarification Counts View (Migration 0020)
// ============================================================================

/**
 * UI-freundlicher Snapshot pro Case. Spiegelt CaseClarificationCountsRow
 * mit lesbareren Namen und garantiert null-safe (last_activity_at).
 */
export interface ClarificationCounts {
  readonly case_id: string;
  readonly unanswered: number;
  readonly awaiting_resolution: number;
  readonly resolved: number;
  readonly total: number;
  readonly last_activity_at: string | null;
}

/**
 * Lädt aggregierte Clarification-Counts für eine Liste von Case-IDs in
 * EINEM Roundtrip via View `case_clarification_counts` (Migration 0020).
 *
 * Ersetzt die N+1-Variante aus supabaseClarifications.getClarificationCountsForCase()
 * für Cockpit-Listen.
 *
 * Returns Map<caseId, counts>. Cases ohne Clarifications haben keinen
 * Eintrag in der Map — Caller MUSS defensiv mit `map.get(id) ?? defaultCounts`
 * arbeiten.
 */
export async function getClarificationCountsForCases(
  caseIds: ReadonlyArray<string>,
): Promise<Map<string, ClarificationCounts>> {
  const result = new Map<string, ClarificationCounts>();
  if (caseIds.length === 0) return result;

  // Filter ungültige UUIDs out, damit wir keine Server-Errors provozieren
  const validIds = caseIds.filter((id) => UUID_RE.test(id));
  if (validIds.length === 0) return result;

  const sb = getSupabaseClient();
  // Cast `as never`: die View `case_clarification_counts` (Migration 0020)
  // ist im hand-gepflegten Database-Schema bewusst nicht eingehängt
  // (siehe supabaseTypes.ts → Views), weil sonst die supabase-js v2
  // GenericSchema-Inferenz für ALLE Tables auf any zurückfallen würde
  // (Relationships-Felder fehlen). Wir typisieren die Antwort lokal über
  // CaseClarificationCountsRow — RLS via security_invoker auf der View
  // sichert die Berechtigung serverseitig.
  const { data, error } = await sb
    .from('case_clarification_counts' as never)
    .select('*')
    .in('case_id', validIds);

  if (error) {
    throw new Error(`getClarificationCountsForCases [${error.code ?? '??'}]: ${error.message}`);
  }

  const rows = (data ?? []) as ReadonlyArray<CaseClarificationCountsRow>;
  for (const row of rows) {
    result.set(row.case_id, {
      case_id: row.case_id,
      unanswered: row.unanswered,
      awaiting_resolution: row.awaiting_resolution,
      resolved: row.resolved,
      total: row.total,
      last_activity_at: row.last_activity_at,
    });
  }
  return result;
}

// ============================================================================
// Pure helpers (für Tests)
// ============================================================================

/**
 * Sanitisiert + validiert eine reason-String für DSGVO-Requests. Reine
 * Funktion — keine DB-Roundtrip. Für Frontend-Form-Validation nutzbar.
 *
 * @returns trimmed string oder null wenn leer
 * @throws Error wenn länger als REASON_MAX_LEN
 */
export function sanitizeDsgvoReason(reason: string | null | undefined): string | null {
  if (reason === null || reason === undefined) return null;
  const trimmed = reason.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > REASON_MAX_LEN) {
    throw new Error(`reason ist auf ${REASON_MAX_LEN.toString()} Zeichen begrenzt`);
  }
  return trimmed;
}

/**
 * Baut einen sicheren Dateinamen für den DSGVO-Datenexport.
 * Reine Funktion — testbar ohne Browser-Setup.
 */
export function buildExportFilename(caseId: string, isoDate: string): string {
  if (!UUID_RE.test(caseId)) {
    throw new Error('buildExportFilename: caseId ist kein UUID-Format');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    throw new Error('buildExportFilename: isoDate muss YYYY-MM-DD sein');
  }
  return `dsgvo-datenexport-${caseId.slice(0, 8)}-${isoDate}.json`;
}

/** Deutsche Bezeichnung des request_type (für UI-Anzeige). */
export function dsgvoRequestTypeLabel(type: DsgvoRequestType): string {
  switch (type) {
    case 'data_export':
      return 'Datenexport (Art. 15 DSGVO)';
    case 'deletion':
      return 'Datenlöschung (Art. 17 DSGVO)';
    case 'consent_withdrawal':
      return 'Widerruf einer Einwilligung';
    case 'rectification':
      return 'Berichtigung (Art. 16 DSGVO)';
  }
}

/** Deutsche Bezeichnung des status (für UI-Anzeige). */
export function dsgvoRequestStatusLabel(status: DsgvoRequestStatus): string {
  switch (status) {
    case 'open':
      return 'offen';
    case 'in_progress':
      return 'in Bearbeitung';
    case 'fulfilled':
      return 'erfüllt';
    case 'rejected':
      return 'abgelehnt';
    case 'expired':
      return 'abgelaufen';
  }
}

// ============================================================================
// Open-Counts pro Case (Bulk) — für Cases-Liste-Badges
// ============================================================================

/**
 * Snapshot der ältesten offenen DSGVO-Anfrage pro Case. Dient den Cases-Liste-
 * Badges: Berater sieht „DSGVO-Anfrage offen — noch X Tage" pro Case.
 */
export interface OpenDsgvoSnapshot {
  /** Anzahl offener Anfragen (status IN ('open', 'in_progress')). */
  readonly count: number;
  /** Frist der dringendsten (kleinster legal_deadline_at). */
  readonly nearestDeadline: string;
  /** True wenn die nearestDeadline bereits abgelaufen ist. */
  readonly isExpired: boolean;
}

/**
 * Lädt offene DSGVO-Anfragen-Snapshots für eine Liste von Case-IDs in einem
 * Roundtrip (statt N+1). Returns Map<caseId, snapshot>; Cases ohne offene
 * Anfragen fehlen in der Map (Caller defaultet via `map.get(id) ?? null`).
 *
 * Performance: für die MVP-Pilot-Skalen (max ~50 Cases pro Berater) reicht
 * ein einziger SELECT mit IN-Filter und client-seitiger Aggregation. Für
 * größere Skalen wäre ein DB-View analog zu case_clarification_counts der
 * sauberere Pfad — Phase 3.
 *
 * RLS: SELECT auf dsgvo_requests filtert serverseitig auf can_access_case().
 * Cases, die der Aufrufer nicht sehen darf, kommen nicht zurück — das ist
 * korrekt.
 *
 * Defensive: bei Backend-Fehler fängt der Caller das (try/catch) und zeigt
 * einfach keine Badges. Wir loggen warn() statt throw, damit ein einzelner
 * Cases-Liste-Render nicht hart bricht.
 */
export async function getOpenDsgvoRequestSnapshotsForCases(
  caseIds: ReadonlyArray<string>,
): Promise<Map<string, OpenDsgvoSnapshot>> {
  const result = new Map<string, OpenDsgvoSnapshot>();
  if (caseIds.length === 0) return result;

  const validIds = caseIds.filter((id) => UUID_RE.test(id));
  if (validIds.length === 0) return result;

  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('dsgvo_requests')
    .select('case_id, status, legal_deadline_at')
    .in('case_id', validIds)
    .in('status', ['open', 'in_progress']);

  if (error) {
    throw new Error(
      `getOpenDsgvoRequestSnapshotsForCases [${error.code ?? '??'}]: ${error.message}`,
    );
  }

  type Row = Pick<DsgvoRequestRow, 'case_id' | 'status' | 'legal_deadline_at'>;
  const rows = (data ?? []) as ReadonlyArray<Row>;
  const now = Date.now();
  for (const row of rows) {
    const existing = result.get(row.case_id);
    const rowDeadlineMs = Date.parse(row.legal_deadline_at);
    if (!existing) {
      result.set(row.case_id, {
        count: 1,
        nearestDeadline: row.legal_deadline_at,
        isExpired: rowDeadlineMs < now,
      });
    } else {
      const existingDeadlineMs = Date.parse(existing.nearestDeadline);
      const useThisDeadline = rowDeadlineMs < existingDeadlineMs;
      result.set(row.case_id, {
        count: existing.count + 1,
        nearestDeadline: useThisDeadline ? row.legal_deadline_at : existing.nearestDeadline,
        isExpired: useThisDeadline ? rowDeadlineMs < now : existing.isExpired,
      });
    }
  }
  return result;
}

// ============================================================================
// Consent-Records (lesend) — für Mandanten-Self-Service-Widerruf
// ============================================================================

/**
 * Manueller Trigger für Bestands-Cases die noch keine consent_records haben.
 *
 * Ruft die SECURITY-DEFINER-RPC `provision_consent_records_for_mandant`
 * (Migration 0022). Idempotent — mehrfaches Aufrufen ist safe.
 *
 * Use-Cases:
 *   - DB-Restore aus Backup ohne consent_records
 *   - UI-Auto-Repair wenn die consent-Liste leer ist
 *     (siehe MandantDsgvoSelfService)
 *   - Migrations-Lücke (vor 0022 wurden consent_records nirgends angelegt)
 *
 * Sicherheits-Garantien (DB):
 *   - Function ist SECURITY DEFINER + search_path-locked.
 *   - Silent return wenn Case nicht existiert oder kein Mandant verknüpft —
 *     keine Privilege-Eskalation möglich.
 *   - GRANT EXECUTE ist auf `authenticated` beschränkt.
 *
 * Für UI-Calls aus dem Mandanten-Self-Service: bei Fehlern nur warnen,
 * nicht throwen — die Page soll trotzdem rendern (best-effort-Repair).
 */
export async function ensureConsentRecordsForCase(caseId: string): Promise<void> {
  if (!UUID_RE.test(caseId)) {
    throw new Error('ensureConsentRecordsForCase: caseId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const { error } = await sb.rpc('provision_consent_records_for_mandant', {
    p_case_id: caseId,
  } as never);

  if (error) {
    throw new Error(`ensureConsentRecordsForCase [${error.code ?? '??'}]: ${error.message}`);
  }
}

/**
 * Listet alle Consent-Records eines Case (RLS: User sieht nur eigene).
 * Wird in der Mandant-DSGVO-Self-Service-Page für die Widerruf-Liste genutzt.
 */
export async function listConsentRecordsForCase(caseId: string): Promise<ConsentRecordRow[]> {
  if (!UUID_RE.test(caseId)) return [];
  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('consent_records')
    .select('*')
    .eq('case_id', caseId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`listConsentRecordsForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data ?? [];
}

// ============================================================================
// Pure-Function-Helpers für UI-Logik (Frist-Countdown, Type-Color-Mapping)
// ============================================================================

/**
 * Berechnet die Tage bis zur DSGVO-Frist relativ zu `now`. Liefert
 * eine negative Zahl, wenn die Frist bereits abgelaufen ist.
 *
 * Pure Function, deterministisch. Wir runden auf Tage AB (floor), damit die
 * Anzeige „noch X Tage" konservativ ist (keine 0,9 → 1).
 */
export function daysUntilDeadline(deadlineIso: string, now: Date = new Date()): number {
  const deadlineMs = Date.parse(deadlineIso);
  const nowMs = now.getTime();
  if (Number.isNaN(deadlineMs)) return 0;
  const diffMs = deadlineMs - nowMs;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

/**
 * Liefert ein Tailwind-Badge-Class-Triple für eine DSGVO-Frist:
 *   - rot wenn abgelaufen
 *   - amber wenn < 7 Tage Restzeit
 *   - stone sonst
 *
 * Pure Function für deterministische Tests.
 */
export function deadlineBadgeKind(daysRemaining: number): 'expired' | 'urgent' | 'normal' {
  if (daysRemaining < 0) return 'expired';
  if (daysRemaining < 7) return 'urgent';
  return 'normal';
}

/**
 * Tailwind-Klassen pro DSGVO-Type für die farbliche Sektions-Markierung
 * im Berater-Cockpit. Sky / Red / Amber / Stone — siehe Design-Spec.
 */
export function dsgvoRequestTypeBadgeClass(type: DsgvoRequestType): string {
  switch (type) {
    case 'data_export':
      return 'bg-sky-100 text-sky-900';
    case 'deletion':
      return 'bg-red-100 text-red-900';
    case 'consent_withdrawal':
      return 'bg-amber-100 text-amber-900';
    case 'rectification':
      return 'bg-stone-100 text-stone-900';
  }
}
